// /admin/licences: the licence register for account administrators (0021).
//
// Lists the people on the administrator's sites with their licences, filterable by person,
// role and status. Licences are role-based and company-wide (HSE Manager decision,
// 4 November 2026): a licence for a role is valid at every BadrEOR location and unit, so
// recording one asks for no location. An administrator records a licence (person, role,
// dates, evidence reference) and withdraws one (reason, effective now or later). Every
// change is a command the server checks and records with who and when; keeping the
// register never gives the administrator authority to sign (SYS-004), and their own
// licences cannot be changed here.
import { useId, useState } from 'react'
import { DateInput, DateTimeInput } from '@/components/DateTimeInput'
import { ErrorBanner } from '@/components/evidence'
import { Badge, Banner, Button, Card, Checkbox, EmptyState, Field, PageHeader, RadioGroup, Select, Sheet, Spinner, TextArea, TextInput } from '@/components/ui'
import type { LicenceRegister } from '@/domain/types'
import { roleLabel } from '@/features/home/permitList'
import { fmtDate, fmtDateTime, fromLocalInput } from '@/lib/format'
import { useCommand, useLicenceRegister, useMe } from '@/lib/queries'
import {
  canWithdraw,
  custodianText,
  EMPTY_FILTER,
  emptyRecordForm,
  filterLicences,
  KIND_LABEL,
  type Licence,
  type LicenceFilter,
  RECORDABLE_ROLES,
  recordArgs,
  type RecordForm,
  roleFilterOptions,
  scopeSummary,
  STATUS_LABEL,
  STATUS_TONE,
} from './model'

export { canWithdraw, custodianText, filterLicences, recordArgs, scopeSummary } from './model'

type Unit = LicenceRegister['sites'][number]['units'][number]

export const isAccountAdmin = (caps: readonly string[] | undefined): boolean => Boolean(caps?.includes('administer_accounts'))

export function LicenceRegisterPage() {
  const me = useMe()
  const admin = isAccountAdmin(me.data?.global_capabilities)
  const q = useLicenceRegister(admin)
  const [filter, setFilter] = useState<LicenceFilter>(EMPTY_FILTER)
  const [recording, setRecording] = useState(false)
  const [withdrawing, setWithdrawing] = useState<Licence | null>(null)
  const [custodianUnit, setCustodianUnit] = useState<Unit | null>(null)
  const ids = { person: useId(), role: useId(), status: useId() }

  if (me.isPending || (admin && q.isPending)) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    )
  }
  if (!admin) {
    return (
      <div className="max-w-3xl">
        <PageHeader title="Licence register" />
        <Banner tone="info" title="Only an account administrator can see this page">
          Your own licences are listed on the Home page.
        </Banner>
      </div>
    )
  }
  if (!q.data) return <ErrorBanner error={q.error ?? new Error('The licence register is not available.')} title="Could not load the licence register" />

  const data = q.data
  const people = new Map(data.people.map((p) => [p.person_id, p]))
  const rows = filterLicences(data.licences, filter)
  const roles = roleFilterOptions(data.licences)
  const filtered = Boolean(filter.person || filter.role || filter.status)

  return (
    <div className="space-y-4" data-testid="licence-register">
      <PageHeader
        title="Licence register"
        subtitle={`${data.licences.length} ${data.licences.length === 1 ? 'licence' : 'licences'} for people on ${data.sites.map((s) => s.name).join(', ') || 'your sites'}.`}
        actions={
          <Button onClick={() => setRecording(true)} data-testid="open-record-licence">
            Record a licence
          </Button>
        }
      />
      <Banner tone="info" title="Licences decide who may sign in a PTW role">
        A licence is for a role and is valid at every BadrEOR location and unit, including units added later. Recording or
        withdrawing a licence is checked and kept with your name and the time; it never lets you sign a permit, and you cannot
        change your own licences.
      </Banner>

      <Card>
        <div className="grid gap-x-4 sm:grid-cols-3" role="group" aria-label="Filter licences">
          <Field label="Person" htmlFor={ids.person}>
            <Select
              id={ids.person}
              data-testid="filter-person"
              value={filter.person}
              onChange={(e) => setFilter({ ...filter, person: e.target.value })}
              placeholder="Everyone"
              options={data.people.map((p) => ({ value: p.person_id, label: p.display_name }))}
            />
          </Field>
          <Field label="Role" htmlFor={ids.role}>
            <Select
              id={ids.role}
              data-testid="filter-role"
              value={filter.role}
              onChange={(e) => setFilter({ ...filter, role: e.target.value })}
              placeholder="All roles"
              options={roles.map((r) => ({ value: r, label: roleLabel(r) }))}
            />
          </Field>
          <Field label="Status" htmlFor={ids.status}>
            <Select
              id={ids.status}
              data-testid="filter-status"
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value as LicenceFilter['status'] })}
              placeholder="Any status"
              options={(['current', 'expired', 'revoked'] as const).map((s) => ({ value: s, label: STATUS_LABEL[s] }))}
            />
          </Field>
        </div>
        <p className="text-sm text-ink-soft" aria-live="polite" data-testid="licence-count">
          Showing {rows.length} of {data.licences.length}.
          {filtered ? (
            <>
              {' '}
              <button type="button" className="font-semibold text-brand underline" onClick={() => setFilter(EMPTY_FILTER)}>
                Clear the filters
              </button>
            </>
          ) : null}
        </p>
      </Card>

      {data.licences.length === 0 ? (
        <EmptyState title="No licences on your sites yet">Record the first one with "Record a licence".</EmptyState>
      ) : rows.length === 0 ? (
        <EmptyState title="No licence matches these filters">Change the person, role or status filter to see more.</EmptyState>
      ) : (
        <ul className="grid gap-3 lg:grid-cols-2" aria-label="Licences" data-testid="licence-list">
          {rows.map((l) => {
            const p = people.get(l.person_id)
            return (
              <li key={l.credential_id} data-testid="licence-row" data-person={p?.display_name ?? ''} data-role={l.role_code} data-status={l.status}>
                <Card className="h-full">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold break-words">{p?.display_name ?? 'Unknown person'}</p>
                      <p className="text-sm text-ink-soft">
                        {roleLabel(l.role_code)} - {KIND_LABEL[l.credential_kind] ?? l.credential_kind}
                      </p>
                    </div>
                    <Badge tone={STATUS_TONE[l.status] ?? 'warn'}>{STATUS_LABEL[l.status] ?? l.status}</Badge>
                  </div>
                  <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                    <div className="col-span-2">
                      <dt className="text-ink-soft">Valid at</dt>
                      <dd data-testid="licence-scope">{scopeSummary(l)}</dd>
                    </div>
                    <div>
                      <dt className="text-ink-soft">Issued</dt>
                      <dd>{fmtDate(l.issued_on)}</dd>
                    </div>
                    <div>
                      <dt className="text-ink-soft">Expires</dt>
                      <dd>{l.expires_on ? fmtDate(l.expires_on) : 'Not stated'}</dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-ink-soft">Evidence reference</dt>
                      <dd className="break-words">{l.evidence_reference ?? '-'}</dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-ink-soft">Recorded</dt>
                      <dd>{l.recorded ? `${fmtDateTime(l.recorded.at)} by ${l.recorded.by ?? 'an administrator'}` : `Issued by ${l.issuer} (before the register)`}</dd>
                    </div>
                    {l.withdrawal ? (
                      <div className="col-span-2" data-testid="licence-withdrawal">
                        <dt className="text-ink-soft">{Date.parse(l.withdrawal.effective_at) <= Date.parse(data.generated_at) ? 'Withdrawn' : 'Withdrawal from'}</dt>
                        <dd>
                          {fmtDateTime(l.withdrawal.effective_at)}: {l.withdrawal.reason}
                          {l.withdrawal.by ? ` (recorded by ${l.withdrawal.by})` : ''}
                        </dd>
                      </div>
                    ) : null}
                  </dl>
                  {canWithdraw(l, data.generated_at) ? (
                    <div className="mt-3">
                      <Button variant="secondary" onClick={() => setWithdrawing(l)} data-testid="open-withdraw">
                        Withdraw this licence
                      </Button>
                    </div>
                  ) : l.is_mine ? (
                    <p className="mt-3 text-[13px] text-ink-soft">Your own licence: another administrator changes it.</p>
                  ) : null}
                </Card>
              </li>
            )
          })}
        </ul>
      )}
      <Card>
        <h2 className="display text-lg font-bold text-ink">Unit custodians</h2>
        <p className="mt-1 text-sm text-ink-soft">
          Each unit's responsible RS, or a named deputy, gives the Other Affected Custodian agreement when another permit's work
          affects the unit. They also need a current RS licence. A unit with nobody named cannot give its agreement.
        </p>
        {data.sites.map((s) => (
          <div key={s.id} className="mt-3">
            <p className="font-semibold">{s.name}</p>
            <ul className="mt-1 divide-y divide-line rounded-lg border border-line bg-white" data-testid="unit-custodians">
              {s.units.map((u) => (
                <li key={u.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm" data-testid="unit-custodian-row" data-unit={u.name}>
                  <span className="min-w-0">
                    <span className="font-semibold">{u.name}</span>
                    <span className={u.custodian ? 'block text-ink' : 'block font-semibold text-bad'}>{custodianText(u.custodian)}</span>
                    {u.custodian ? (
                      <span className="block text-xs text-ink-soft">
                        Since {fmtDateTime(u.custodian.effective_from)}, named by {u.custodian.assigned_by}
                        {u.custodian.reason ? `: ${u.custodian.reason}` : ''}
                      </span>
                    ) : null}
                    {u.scheduled ? (
                      <span className="block text-xs text-ink-soft">
                        From {fmtDateTime(u.scheduled.effective_from)}: {custodianText(u.scheduled)}
                      </span>
                    ) : null}
                  </span>
                  <Button variant="ghost" onClick={() => setCustodianUnit(u)} data-testid="open-unit-custodian">
                    Change
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </Card>
      <p className="text-xs text-ink-soft">Updated {fmtDateTime(data.generated_at)}.</p>

      {recording ? <RecordLicenceSheet data={data} onClose={() => setRecording(false)} /> : null}
      {withdrawing ? (
        <WithdrawSheet licence={withdrawing} personName={people.get(withdrawing.person_id)?.display_name ?? 'this person'} onClose={() => setWithdrawing(null)} />
      ) : null}
      {custodianUnit ? <CustodianSheet unit={custodianUnit} data={data} onClose={() => setCustodianUnit(null)} /> : null}
    </div>
  )
}

function CustodianSheet({ unit, data, onClose }: { unit: Unit; data: LicenceRegister; onClose: () => void }) {
  const cmd = useCommand('set_unit_custodian')
  const [rs, setRs] = useState(unit.custodian?.responsible.id ?? '')
  const [deputies, setDeputies] = useState<string[]>(unit.custodian?.deputies.map((d) => d.id) ?? [])
  const [when, setWhen] = useState<'now' | 'later'>('now')
  const [later, setLater] = useState('')
  const [reason, setReason] = useState('')
  const id = useId()
  // People with a current RS licence first; anyone on the sites may be named, the agreement checks the licence.
  const rsHolders = new Set(data.licences.filter((l) => l.role_code === 'RS' && l.status === 'current').map((l) => l.person_id))
  const choices = data.people
    .filter((p) => !p.is_me)
    .sort((a, b) => Number(rsHolders.has(b.person_id)) - Number(rsHolders.has(a.person_id)) || a.display_name.localeCompare(b.display_name))
  const label = (p: (typeof choices)[number]) => `${p.display_name}${rsHolders.has(p.person_id) ? ' - current RS licence' : ' - no current RS licence'}`
  async function submit() {
    try {
      await cmd.run({
        site_area_id: unit.id,
        responsible_rs_person_id: rs,
        deputy_person_ids: deputies.filter((d) => d !== rs),
        effective_from: when === 'later' ? fromLocalInput(later) : null,
        reason: reason.trim() ? reason : null,
      })
      onClose()
    } catch {
      // Shown below.
    }
  }
  return (
    <Sheet open title={`Custodian of ${unit.name}`} onClose={onClose}>
      <p className="mb-3 text-sm text-ink-soft">
        The responsible RS and any deputies give this unit's agreement to other permits. An agreement already given stays as it was.
      </p>
      <Field label="Responsible RS" htmlFor={`${id}-rs`} required>
        <Select id={`${id}-rs`} data-testid="custodian-rs" value={rs} onChange={(e) => setRs(e.target.value)} placeholder="Choose a person" options={choices.map((p) => ({ value: p.person_id, label: label(p) }))} />
      </Field>
      <Field label="Deputies (optional)">
        <div className="rounded-lg border border-line bg-white px-3" data-testid="custodian-deputies">
          {choices.filter((p) => p.person_id !== rs && rsHolders.has(p.person_id)).map((p) => (
            <Checkbox
              key={p.person_id}
              checked={deputies.includes(p.person_id)}
              onChange={(on) => setDeputies(on ? [...deputies, p.person_id] : deputies.filter((d) => d !== p.person_id))}
              label={p.display_name}
            />
          ))}
        </div>
      </Field>
      <Field label="Takes effect" required>
        <RadioGroup name={`${id}-when`} value={when} onChange={setWhen} options={[{ value: 'now', label: 'Now' }, { value: 'later', label: 'From a later date and time' }]} />
      </Field>
      {when === 'later' ? (
        <Field label="From (Muscat time)" htmlFor={`${id}-later`} required>
          <DateTimeInput id={`${id}-later`} name="Assignment takes effect from" value={later} onChange={setLater} />
        </Field>
      ) : null}
      <Field label="Note (optional)" htmlFor={`${id}-reason`}>
        <TextArea id={`${id}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      {cmd.error ? <ErrorBanner error={cmd.error} title="The custodian was not changed" /> : null}
      <div className="mt-3 flex gap-2">
        <Button loading={cmd.isPending} disabled={!rs} onClick={() => void submit()} data-testid="custodian-submit">
          Save the custodian
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Sheet>
  )
}

function RecordLicenceSheet({ data, onClose }: { data: LicenceRegister; onClose: () => void }) {
  const cmd = useCommand('register_credential_evidence')
  const [f, setF] = useState<RecordForm>(emptyRecordForm)
  const [done, setDone] = useState(false)
  const id = useId()
  const set = (patch: Partial<RecordForm>) => setF((x) => ({ ...x, ...patch }))
  const fieldError = (name: string) => cmd.error?.blockers.find((x) => x.field === name)?.message

  async function submit() {
    try {
      await cmd.run(recordArgs(f))
      setDone(true)
    } catch {
      // Shown below from cmd.error.
    }
  }

  return (
    <Sheet open title="Record a licence" onClose={onClose}>
      {done ? (
        <div className="space-y-3" data-testid="record-licence-done">
          <Banner tone="ok" title="Licence recorded">
            It is in the register with your name and the time, valid at every BadrEOR location, and the server uses it at the
            next signature.
          </Banner>
          <Button onClick={onClose}>Close</Button>
        </div>
      ) : (
        <form
          data-testid="record-licence-form"
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
        >
          <p className="mb-3 text-sm text-ink-soft">The licence is for a role and is valid at every BadrEOR location and unit.</p>
          <Field label="Person" htmlFor={`${id}-person`} required>
            <Select
              id={`${id}-person`}
              data-testid="record-person"
              value={f.person_id}
              onChange={(e) => set({ person_id: e.target.value })}
              placeholder="Choose a person"
              options={data.people.filter((p) => !p.is_me).map((p) => ({ value: p.person_id, label: `${p.display_name}${p.organisation ? ` - ${p.organisation}` : ''}` }))}
            />
          </Field>
          <Field label="Role" htmlFor={`${id}-role`} required error={fieldError('role_code')}>
            <Select
              id={`${id}-role`}
              data-testid="record-role"
              value={f.role_code}
              onChange={(e) => set({ role_code: e.target.value })}
              placeholder="Choose the role"
              options={RECORDABLE_ROLES.map((r) => ({ value: r, label: roleLabel(r) }))}
            />
          </Field>
          <div className="grid gap-x-4 sm:grid-cols-2">
            <Field label="Issued on" htmlFor={`${id}-issued`} required error={fieldError('issued_on')}>
              <DateInput id={`${id}-issued`} name="Issued on" value={f.issued_on} onChange={(v) => set({ issued_on: v })} />
            </Field>
            <Field label="Expires on" htmlFor={`${id}-expires`} required={!f.expiry_not_stated} error={fieldError('expires_on')}>
              <DateInput id={`${id}-expires`} name="Expires on" value={f.expires_on} disabled={f.expiry_not_stated} onChange={(v) => set({ expires_on: v })} />
            </Field>
          </div>
          <Checkbox
            checked={f.expiry_not_stated}
            onChange={(v) => set({ expiry_not_stated: v, expires_on: v ? '' : f.expires_on })}
            label="The evidence states no expiry date"
            hint="The licence is then not accepted for signing until the policy approves licences without a stated expiry."
          />
          <Field label="Evidence reference" htmlFor={`${id}-evidence`} required error={fieldError('evidence_reference')} hint="Licence or certificate number, register entry or document reference.">
            <TextInput id={`${id}-evidence`} data-testid="record-evidence" value={f.evidence_reference} onChange={(e) => set({ evidence_reference: e.target.value })} />
          </Field>
          <Field label="Issued by (optional)" htmlFor={`${id}-issuer`} error={fieldError('issuer')}>
            <TextInput id={`${id}-issuer`} value={f.issuer} onChange={(e) => set({ issuer: e.target.value })} />
          </Field>
          {cmd.error ? <ErrorBanner error={cmd.error} title="The licence was not recorded" /> : null}
          <div className="mt-3 flex gap-2">
            <Button type="submit" loading={cmd.isPending} disabled={!f.person_id} data-testid="record-licence-submit">
              Record the licence
            </Button>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Sheet>
  )
}

function WithdrawSheet({ licence, personName, onClose }: { licence: Licence; personName: string; onClose: () => void }) {
  const cmd = useCommand('revoke_credential')
  const [reason, setReason] = useState('')
  const [when, setWhen] = useState<'now' | 'later'>('now')
  const [later, setLater] = useState('')
  const id = useId()
  async function submit() {
    try {
      await cmd.run({ credential_id: licence.credential_id, reason, effective_at: when === 'later' ? fromLocalInput(later) : null })
      onClose()
    } catch {
      // Shown below.
    }
  }
  return (
    <Sheet open title={`Withdraw ${personName}'s ${roleLabel(licence.role_code)} licence`} onClose={onClose}>
      <p className="mb-3 text-sm text-ink-soft">
        The withdrawal is added to the record; the licence itself and earlier signatures stay as they were.
      </p>
      <Field label="Reason" htmlFor={`${id}-reason`} required>
        <TextArea id={`${id}-reason`} data-testid="withdraw-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <Field label="Takes effect" required>
        <RadioGroup
          name={`${id}-when`}
          value={when}
          onChange={setWhen}
          options={[
            { value: 'now', label: 'Now' },
            { value: 'later', label: 'From a later date and time' },
          ]}
        />
      </Field>
      {when === 'later' ? (
        <Field label="From (Muscat time)" htmlFor={`${id}-later`} required>
          <DateTimeInput id={`${id}-later`} name="Withdrawal takes effect from" value={later} onChange={setLater} />
        </Field>
      ) : null}
      {cmd.error ? <ErrorBanner error={cmd.error} title="The licence was not withdrawn" /> : null}
      <div className="mt-3 flex gap-2">
        <Button variant="danger" loading={cmd.isPending} onClick={() => void submit()} data-testid="withdraw-submit">
          Withdraw the licence
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Sheet>
  )
}
